import {
  DocumentSnapshot,
  FirestoreDataConverter,
  SnapshotOptions,
  WithFieldValue
} from "firebase/firestore";
import { VEHICLE_TIERS, Vehicle } from "../inventory";

const vehicleConverter: FirestoreDataConverter<Vehicle> = {
  toFirestore(vehicle: WithFieldValue<Vehicle>) {
    const { id, ...data } = vehicle;
    return {
      ...data,
    };
  },
  fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions): Vehicle {
    const data = snapshot.data(options);
    const safeNumber = (value: any): number | null => {
      if (!value) return null;
      const num = Number(value);
      return isNaN(num) ? null : num;
    };

    // safeNumber treats 0 as missing. Estimated repair amounts use 0 as a real value.
    const safeAmount = (value: unknown): number | null => {
      if (value === null || value === undefined || value === '') return null;
      const num = Number(value);
      return Number.isNaN(num) ? null : num;
    };

    const safeDate = (value: any): string | null => {
      if (!value) return null;

      // Handle "TBD" values - return as-is
      if (value === 'TBD') {
        return 'TBD';
      }

      // If it's already a Date object
      if (value instanceof Date) {
        return value.toISOString();
      }

      // If it's a Firestore Timestamp
      if (value && typeof value.toDate === 'function') {
        return value.toDate().toISOString();
      }

      // If it's a string that can be parsed as a date
      if (typeof value === 'string') {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        } catch (e) {
          // Invalid date string
          return null;
        }
      }

      return null;
    };

    // if no data, return a new vehicle with all null values
    if (!data)
      return {
        id: snapshot.id,
        date_added: null,
        entered_by: "",
        vin: "",
        status: null,
        stock_number: "",
        year: null,
        make: null,
        model: null,
        size: null,
        type: null,
        miles: null,
        fuel: null,
        condition: null,
        tier: null,
        color: 'White',
        optic_list_price: null,
        seller_asking_price: null,
        profit: null,
        profit_realized: false,
        payoff_amount: null,
        repair_cost: 0,
        shipping_cost: 0,
        rental_cost: 0,
        other_cost: 0,
        sold_date: null,
        date_available: null,
        who: null,
        sold_to_user: null,
        sold_to_contact_id: null,
        sold_by_user: null,
        selling_to_user: null,
        selling_to_contact_id: null,
        sold_location: null,
        pending_sale_date: null,
        active_deal_date: null,
        pending_available_date: null,
        available_date: null,
        dealQuality: null,
        seller_type: null,
        auction_location_id: null,
        title_format: null,
        title_state: null,
        exclude_from_title_emails: false,
        has_lien: null,
        lien_lender: null,
        lien_payoff_amount: null,
        lien_good_through_date: null,
        lien_account_number: null,
        bought_from: null,
        location: null,
        phone_number: null,
        email: null,
        truck_notes: null,
        follow_up_notes: null,
        document_notes: null,
        estimated_body: null,
        estimated_mechanical: null,
        estimated_safety: null,
        estimated_repair_cost: null,
        estimated_repair_addons: [],
        repair_cost_over_budget_reason: null,
        link: null,
        photo_storage_path: null,
        show_on_website: false,
        updated_by: null,
        updated_at: null,
        cameras: 'unknown',
        shelves: 'unknown',
        gwvr_over_10k: 'unknown',
        size_confirmed: false,
        favorite: false,
        purchased_for_inventory: false,
        purchased_for_inventory_date: null,
        pending_sale_not_purchased_notify: false,
        paid_for: false,
        paid_for_date: null,
        funded: false,
        funded_date: null,
        translease_lease: false,
        payroll_omitted_user_ids: null,
        delivered_to_customer: false,
        finance: null,
        shipping: null,
        refinance: false,
        pickup_location: null,
        rear_wheel: null,
        documents: {
          title_copies: [],
          purchase_orders: [],
        },
        ready_for_transport: null,
        mechanical_status: 'N/A',
        body_status: 'N/A',
        ready_for_sale: undefined,
      };

    return {
      // Core identification fields
      id: snapshot.id,
      date_added: safeDate(data.date_added),
      entered_by: data.entered_by || "",
      vin: data.vin || "",
      stock_number: data.stock_number || "",

      // Basic vehicle information
      year: safeNumber(data.year),
      make: data.make || null,
      model: data.model || null,
      size: data.size || null,
      type: data.type || null,
      miles: safeNumber(data.miles),
      fuel: data.fuel || null,
      condition: data.condition || null,
      tier: (VEHICLE_TIERS as readonly string[]).includes(data.tier) ? data.tier as Vehicle['tier'] : null,
      color: ['White', 'Blue', 'Yellow', 'Being painted'].includes(data.color) ? data.color as Vehicle['color'] : 'White',
      being_painted_date: safeDate(data.being_painted_date) ?? null,
      color_history: Array.isArray(data.color_history)
        ? (data.color_history as any[]).map((e: any) => {
            const c = e?.color ?? e?.to
            return {
              color: ['White', 'Blue', 'Yellow', 'Being painted'].includes(c) ? c : 'White',
              changed_at: typeof e?.changed_at === 'string' ? e.changed_at : new Date().toISOString(),
              ...(e?.changed_by_user_id && { changed_by_user_id: e.changed_by_user_id }),
            }
          })
        : undefined,
      gwvr_over_10k: data.gwvr_over_10k || 'unknown',

      // Financial information
      optic_list_price: safeNumber(data.optic_list_price),
      seller_asking_price: safeNumber(data.seller_asking_price),
      profit: safeNumber(data.profit),
      profit_realized: data.profit_realized === true,
      payoff_amount: safeNumber(data.payoff_amount),

      // Vehicle costs
      repair_cost: safeNumber(data.repair_cost) || 0,
      shipping_cost: safeNumber(data.shipping_cost) || 0,
      rental_cost: safeNumber(data.rental_cost) || 0,
      other_cost: safeNumber(data.other_cost) || 0,

      // Sales information
      finance: {
        payment_type: data.finance?.payment_type || null,
        down_payment: safeNumber(data.finance?.down_payment) || null,
        bank_name: data.finance?.bank_name || null,
        amount_financed: safeNumber(data.finance?.amount_financed) || null,
        // Allow 0 — safeNumber treats 0 as falsy and would otherwise drop it to null
        points: (() => {
          const value = data.finance?.points;
          if (value === null || value === undefined || value === '') return null;
          const num = Number(value);
          return isNaN(num) ? null : num;
        })(),
        profit: safeNumber(data.finance?.profit) || null,
        total_sale_price: safeNumber(data.finance?.total_sale_price) || null,
        tax_rate: safeNumber(data.finance?.tax_rate) || null,
        include_tax_in_sale: data.finance?.include_tax_in_sale || false,
        include_warranty_in_sale: data.finance?.include_warranty_in_sale || false,
        include_shipping_in_sale: data.finance?.include_shipping_in_sale || false,
      },
      shipping: {
        shipped: data.shipping?.shipped === true ? true : data.shipping?.shipped === false ? false : null,
        staged: data.shipping?.staged === true ? true : data.shipping?.staged === false ? false : null,
        not_shipped_reason:
          typeof data.shipping?.not_shipped_reason === "string"
            ? data.shipping.not_shipped_reason.trim() || null
            : null,
        our_shipping_cost: safeNumber(data.shipping?.our_shipping_cost) || null,
        customer_shipping_cost: safeNumber(data.shipping?.customer_shipping_cost) || null,
        profit: safeNumber(data.shipping?.profit) || null
      },
      refinance: data.refinance !== undefined ? Boolean(data.refinance) : false,
      sold_date: safeDate(data.sold_date),
      date_available: safeDate(data.date_available),
      who: data.who || null,
      selling_to_user: data.selling_to_user || null,
      selling_to_contact_id:
        typeof data.selling_to_contact_id === "string" && data.selling_to_contact_id.trim()
          ? data.selling_to_contact_id.trim()
          : null,
      sold_to_user: data.sold_to_user || 
        (data.sold_to ? { 
          id: data.who || 'unknown',
          name: data.sold_to
        } : null),
      sold_to_contact_id:
        typeof data.sold_to_contact_id === "string" && data.sold_to_contact_id.trim()
          ? data.sold_to_contact_id.trim()
          : null,
      sold_by_user: data.sold_by_user || 
        (data.sold_by ? { 
          id: data.sold_by_id || 'house',
          name: data.sold_by
        } : null),
      sold_location: data.sold_location || null,
      pending_sale_date: safeDate(data.pending_sale_date) || null,
      active_deal_date: safeDate(data.active_deal_date) || null,
      pending_available_date: safeDate(data.pending_available_date) || null,
      available_date: safeDate(data.available_date) || null,
      status: [
        "Pending Available",
        "Available",
        "Pending Sale",
        "Sold",
        "Sold elsewhere",
        "Active Deal",
      ].includes(data.status || "")
        ? (data.status as Vehicle["status"])
        : null,
      seller_type: [
        "Private Party",
        "Auction",
        "Dealer",
        "Repo",
      ].includes(data.seller_type || "")
        ? (data.seller_type as Vehicle["seller_type"])
        : null,
      auction_location_id:
        typeof data.auction_location_id === "string" && data.auction_location_id.trim()
          ? data.auction_location_id.trim()
          : null,
      title_format: ["electronic", "paper"].includes(data.title_format || "")
        ? (data.title_format as Vehicle["title_format"])
        : null,
      title_state:
        typeof data.title_state === "string" && data.title_state.trim()
          ? data.title_state.trim()
          : null,
      has_lien: data.has_lien !== undefined ? Boolean(data.has_lien) : null,
      lien_lender: data.lien_lender || null,
      lien_payoff_amount: safeNumber(data.lien_payoff_amount),
      lien_good_through_date: safeDate(data.lien_good_through_date),
      lien_account_number: data.lien_account_number || null,
      dealQuality: [
        "great",
        "good",
        "average",
        "belowAverage",
        "poor",
      ].includes(data.dealQuality || "")
        ? (data.dealQuality as Vehicle["dealQuality"])
        : null,

      // Contact and location details
      location: data.location || null,
      phone_number: data.phone_number || null,
      email: data.email || null,
      ...(data.assigned_user && { assigned_user: data.assigned_user }),

      // Notes and additional information
      truck_notes: data.truck_notes || null,
      follow_up_notes: data.follow_up_notes || null,
      document_notes: data.document_notes || null,
      estimated_body: safeAmount(data.estimated_body),
      estimated_mechanical: safeAmount(data.estimated_mechanical),
      estimated_safety: safeAmount(data.estimated_safety),
      estimated_repair_cost: safeAmount(data.estimated_repair_cost),
      estimated_repair_addons: Array.isArray(data.estimated_repair_addons)
        ? data.estimated_repair_addons
            .filter(
              (addon: any) =>
                addon &&
                typeof addon.id === 'string' &&
                (addon.category === 'body' ||
                  addon.category === 'mechanical' ||
                  addon.category === 'safety'),
            )
            .map((addon: any) => {
              const quantityRaw =
                typeof addon.quantity === 'number' && Number.isFinite(addon.quantity)
                  ? addon.quantity
                  : 1
              const quantity = Math.min(20, Math.max(1, Math.floor(quantityRaw)))
              return {
                id: addon.id,
                amount: Number(addon.amount) || 0,
                category: addon.category as 'body' | 'mechanical' | 'safety',
                quantity,
              }
            })
        : [],
      repair_cost_over_budget_reason: data.repair_cost_over_budget_reason || null,
      link: data.link || null,

      // Photo and display properties
      show_on_website: Boolean(data.show_on_website),
      photo_storage_path: data.photo_storage_path || null,
      photos: {
        main: data.photos?.main || null,
        front: data.photos?.front || null,
        back: data.photos?.back || null,
        left: data.photos?.left || null,
        right: data.photos?.right || null,
        interior: data.photos?.interior || null,
        engine: data.photos?.engine || null,
        trunk: data.photos?.trunk || null,
        other: data.photos?.other || null,
      },

      // Equipment details
      cameras: data.cameras || 'unknown',
      shelves: data.shelves || 'unknown',
      size_confirmed: Boolean(data.size_confirmed),
      updated_at: safeDate(data.updated_at),
      updated_by: data.updated_by || null,
      favorite: data.favorite || false,

      // New bought_from field
      bought_from: data.bought_from || null,
      purchased_for_inventory: data.purchased_for_inventory || false,
      purchased_for_inventory_date: safeDate(data.purchased_for_inventory_date) || null,
      pending_sale_not_purchased_notify: Boolean(data.pending_sale_not_purchased_notify),
      paid_for: data.paid_for || false,
      paid_for_date: safeDate(data.paid_for_date),
      funded: data.funded ?? false,
      funded_date: safeDate(data.funded_date) ?? null,
      translease_lease: data.translease_lease === true,
      exclude_from_title_emails: data.exclude_from_title_emails === true,
      payroll_omitted_user_ids: Array.isArray(data.payroll_omitted_user_ids)
        ? data.payroll_omitted_user_ids.filter((id: unknown) => typeof id === 'string')
        : null,
      delivered_to_customer: data.delivered_to_customer || false,
      warranty_purchaged: data?.warranty_purchaged? true: false,
      warranty: data?.warranty_purchaged? data.warranty: null,

      // Pickup location data
      pickup_location: data.pickup_location ? {
        address: data.pickup_location.address || '',
        city: data.pickup_location.city || '',
        state: data.pickup_location.state || '',
        zip: data.pickup_location.zip || '',
        contact_name: data.pickup_location.contact_name || '',
        contact_phone: data.pickup_location.contact_phone || '',
        notes: data.pickup_location.notes || ''
      } : null,
      rear_wheel: data.rear_wheel || null,

      // Document properties
      documents: data.documents ? {
        title_copies: data.documents.title_copies || [],
        purchase_orders: data.documents.purchase_orders || [],
        payment_copies: data.documents.payment_copies || [],
        title_flip_docs: data.documents.title_flip_docs || [],
      } : null,

      // Ready for transport
      ready_for_transport: data.ready_for_transport ? {
        needs_address: Boolean(data.ready_for_transport.needs_address),
        ready_for_pickup: Boolean(data.ready_for_transport.ready_for_pickup),
        customer: data.ready_for_transport.customer !== undefined ? Boolean(data.ready_for_transport.customer) : undefined,
        repair: data.ready_for_transport.repair !== undefined ? Boolean(data.ready_for_transport.repair) : undefined,
        note: data.ready_for_transport.note || null,
        ready_for_transport_date: safeDate(data.ready_for_transport.ready_for_transport_date),
        hidden_from_needs_transport:
          data.ready_for_transport.hidden_from_needs_transport !== undefined
            ? Boolean(data.ready_for_transport.hidden_from_needs_transport)
            : undefined,
      } : null,

      // Gate passes
      gate_passes: data.gate_passes ? (Array.isArray(data.gate_passes) ? data.gate_passes.map((file: any) => ({
        fileName: file.fileName || '',
        storagePath: file.storagePath || '',
        url: file.url || '',
        fileSize: safeNumber(file.fileSize) || 0,
        fileType: file.fileType || '',
        uploadedAt: safeDate(file.uploadedAt) || new Date().toISOString(),
        uploadedBy: file.uploadedBy || '',
      })) : []) : undefined,

      // Title tracking
      title_tracking: data.title_tracking ? {
        paid_date: safeDate(data.title_tracking.paid_date) || null,
        title_status: data.title_tracking.title_status || undefined,
        bank_contacted_date: safeDate(data.title_tracking.bank_contacted_date) || null,
        bank_name: data.title_tracking.bank_name || null,
        tracking_number: data.title_tracking.tracking_number || null, // Legacy field
        inbound_tracking_number: data.title_tracking.inbound_tracking_number || null,
        outbound_tracking_number: data.title_tracking.outbound_tracking_number || null,
        title_requested_date: safeDate(data.title_tracking.title_requested_date) || null,
        in_transit_date: safeDate(data.title_tracking.in_transit_date) || null,
        title_shipped_date: safeDate(data.title_tracking.title_shipped_date) || null,
        physical_title_received_date: safeDate(data.title_tracking.physical_title_received_date) || null,
        title_flip_required: data.title_tracking.title_flip_required === true ? true : data.title_tracking.title_flip_required === false ? false : undefined,
        title_flip_required_date: safeDate(data.title_tracking.title_flip_required_date) || null,
        title_flip_in_progress_date: safeDate(data.title_tracking.title_flip_in_progress_date) || null,
        title_flip_completed_date: safeDate(data.title_tracking.title_flip_completed_date) || null,
        shipping_info_title: data.title_tracking.shipping_info_title === true ? true : data.title_tracking.shipping_info_title === false ? false : undefined,
        shipping_info_reassignment: data.title_tracking.shipping_info_reassignment === true ? true : data.title_tracking.shipping_info_reassignment === false ? false : undefined,
        shipping_info_lien_release: data.title_tracking.shipping_info_lien_release === true ? true : data.title_tracking.shipping_info_lien_release === false ? false : undefined,
        shipping_info_additional_paperwork: data.title_tracking.shipping_info_additional_paperwork === true ? true : data.title_tracking.shipping_info_additional_paperwork === false ? false : undefined,
        shipping_info_completed_date: safeDate(data.title_tracking.shipping_info_completed_date) || null,
        title_in_house_date: safeDate(data.title_tracking.title_in_house_date) || null,
        completed_date: safeDate(data.title_tracking.completed_date) || null,
        notes: data.title_tracking.notes || null,
        issue_reported: data.title_tracking.issue_reported || false,
        issue_description: data.title_tracking.issue_description || null,
        major_issue: data.title_tracking.major_issue || false,
        last_updated_by: data.title_tracking.last_updated_by || null,
        last_updated_at: safeDate(data.title_tracking.last_updated_at) || null,
        revert_note: data.title_tracking.revert_note || null,
        revert_note_at: safeDate(data.title_tracking.revert_note_at) || null,
        revert_note_by: data.title_tracking.revert_note_by || null,
        label_storage_path: data.title_tracking.label_storage_path || null,
        shipping_labels: data.title_tracking.shipping_labels || null,
      } : undefined,

      // Inspection status fields
      mechanical_status: ['N/A', 'Need', 'Pending', 'Complete'].includes(data.mechanical_status || '')
        ? (data.mechanical_status as Vehicle['mechanical_status'])
        : 'N/A',
      body_status: ['N/A', 'Need', 'Pending', 'Complete'].includes(data.body_status || '')
        ? (data.body_status as Vehicle['body_status'])
        : 'N/A',
      ready_for_sale: data.ready_for_sale !== undefined ? Boolean(data.ready_for_sale) : undefined,
    };
  },
};

export { vehicleConverter };
